"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { CatalogItem } from "@/lib/airtable";
import CatalogClient, { type SortOption } from "./CatalogClient";
import { CATEGORY_CONFIG } from "./categoryConfig";
import { buildTaxonomyRegex } from "@/lib/taxonomyMatch";
import { cn } from "@/lib/utils";

// Use an explicit locale so server (Node) and client (browser) produce
// identical formatted strings, avoiding hydration mismatches.
const formatPrice = (n: number) => new Intl.NumberFormat("en-US").format(n);

// ─── Filter option constants ────────────────────────────────────────────────

const STONE_OPTIONS = [
  "Diamond", "Emerald", "Ruby", "Sapphire", "Aquamarine",
  "Rubelite", "Tanzanite", "Garnet", "Amethyst", "Alexandrite",
  "Pearl", "Peridot", "Tourmaline", "Topaz", "Citrine", "Opal", "Black Diamond",
  "Onyx", "Paraiba"
] as const;

const COLOR_OPTIONS = [
  { label: "Blue",    dot: "bg-blue-500" },
  { label: "Green",   dot: "bg-emerald-500" },
  { label: "Red",     dot: "bg-red-500" },
  { label: "Pink",    dot: "bg-pink-400" },
  { label: "Orange",  dot: "bg-orange-400" },
  { label: "Yellow",  dot: "bg-yellow-400" },
  { label: "Rainbow", dot: "bg-gradient-to-r from-red-400 via-yellow-400 to-blue-500" },
  { label: "Purple",  dot: "bg-purple-500" },
  { label: "White",   dot: "bg-white border border-slate-300" },
  { label: "Black",   dot: "bg-slate-900" },
  { label: "Gray",    dot: "bg-slate-400" },
] as const;

const METAL_OPTIONS = ["Yellow Gold", "Rose Gold", "White Gold"] as const;

// ─── Price range slider ─────────────────────────────────────────────────────

function PriceRangeSlider({
  globalMin,
  globalMax,
  priceMin,
  priceMax,
  onMinChange,
  onMaxChange,
}: {
  globalMin: number;
  globalMax: number;
  priceMin: number;
  priceMax: number;
  onMinChange: (v: number) => void;
  onMaxChange: (v: number) => void;
}) {
  const range = globalMax - globalMin || 1;
  const minPct = ((priceMin - globalMin) / range) * 100;
  const maxPct = ((priceMax - globalMin) / range) * 100;

  return (
    <div className="px-1">
      <div className="mb-3 flex justify-between text-sm font-medium text-slate-700">
        <span>${formatPrice(priceMin)}</span>
        <span>{priceMax >= globalMax ? "No limit" : `$${formatPrice(priceMax)}`}</span>
      </div>
      <div className="relative h-1 rounded-full bg-slate-200">
        <div
          className="absolute h-full rounded-full bg-slate-800"
          style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        <input
          type="range"
          min={globalMin}
          max={globalMax}
          step={Math.max(1, Math.floor(range / 200))}
          value={priceMin}
          onChange={(e) => onMinChange(Math.min(Number(e.target.value), priceMax - 1))}
          className="price-range-input"
          style={{ zIndex: priceMin > globalMax - range * 0.1 ? 5 : 3 }}
        />
        <input
          type="range"
          min={globalMin}
          max={globalMax}
          step={Math.max(1, Math.floor(range / 200))}
          value={priceMax}
          onChange={(e) => onMaxChange(Math.max(Number(e.target.value), priceMin + 1))}
          className="price-range-input"
          style={{ zIndex: 4 }}
        />
      </div>
    </div>
  );
}

// ─── FilterSection ──────────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">{title}</p>
      {children}
    </div>
  );
}

// ─── Pill button ─────────────────────────────────────────────────────────────

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[11px] font-medium transition",
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
      )}
    >
      {children}
    </button>
  );
}

// ─── Toggle helper ───────────────────────────────────────────────────────────

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

// ─── Main component ──────────────────────────────────────────────────────────

type Props = {
  items: CatalogItem[];
  showPieceFilter?: boolean;
  backHref?: string;
  backLabel?: string;
};

export function FilteredCatalogClient({ items, showPieceFilter = false, backHref, backLabel }: Props) {
  const { globalMin, globalMax } = useMemo(() => {
    const prices = items
      .map((i) => i.tagPrice)
      .filter((p): p is number => p !== null);
    return {
      globalMin: prices.length ? Math.floor(Math.min(...prices)) : 0,
      globalMax: prices.length ? Math.ceil(Math.max(...prices)) : 10000,
    };
  }, [items]);

  const [open, setOpen] = useState(false);
  // Sold pieces are SHOWN by default: the client wants a shopper who falls for
  // something already gone to see it and ask for one like it. "Hide sold out"
  // is the opt-out for anyone who only wants what they can buy today.
  const [hideSoldOut, setHideSoldOut] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>("popular");
  const [pieceTypes, setPieceTypes] = useState<string[]>([]);
  const [stones, setStones] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [metals, setMetals] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState(globalMin);
  const [priceMax, setPriceMax] = useState(globalMax);

  // Keep price range in sync when items change (e.g. page navigation)
  const [lastGlobalMin, setLastGlobalMin] = useState(globalMin);
  const [lastGlobalMax, setLastGlobalMax] = useState(globalMax);
  if (globalMin !== lastGlobalMin || globalMax !== lastGlobalMax) {
    setLastGlobalMin(globalMin);
    setLastGlobalMax(globalMax);
    setPriceMin(globalMin);
    setPriceMax(globalMax);
  }

  const activeCount =
    pieceTypes.length +
    stones.length +
    colors.length +
    metals.length +
    (priceMin > globalMin || priceMax < globalMax ? 1 : 0);

  const clearAll = () => {
    setPieceTypes([]);
    setStones([]);
    setColors([]);
    setMetals([]);
    setPriceMin(globalMin);
    setPriceMax(globalMax);
  };

  const matched = useMemo(() => {
    let result = items;

    if (pieceTypes.length > 0) {
      result = result.filter((item) =>
        pieceTypes.some((match) => buildTaxonomyRegex(match).test(item.type || ""))
      );
    }

    if (stones.length > 0) {
      result = result.filter((item) =>
        stones.some((s) => item.stone.toLowerCase().includes(s.toLowerCase()))
      );
    }

    if (colors.length > 0) {
      result = result.filter((item) =>
        item.stoneColors.some((c) =>
          colors.some((sel) => c.toLowerCase() === sel.toLowerCase())
        )
      );
    }

    if (metals.length > 0) {
      result = result.filter((item) =>
        metals.some((m) => item.metal.toLowerCase().includes(m.toLowerCase()))
      );
    }

    if (priceMin > globalMin || priceMax < globalMax) {
      result = result.filter(
        (item) =>
          item.tagPrice !== null &&
          item.tagPrice >= priceMin &&
          item.tagPrice <= priceMax
      );
    }

    return result;
  }, [items, pieceTypes, stones, colors, metals, priceMin, priceMax, globalMin, globalMax]);

  // Availability is applied last and kept separate from `matched` so the empty
  // state can tell "nothing matches your filters" apart from "everything that
  // matches has sold" — the second is worth offering Show all for.
  //
  // Filter on `soldOut`, never on status === "sold": soldOut also covers
  // returned-to-India pieces, which are equally unbuyable (lib/itemStatus.ts).
  // "On order" pieces (inPossession false) are still purchasable and stay.
  const filtered = useMemo(
    () => (hideSoldOut ? matched.filter((item) => !item.soldOut) : matched),
    [matched, hideSoldOut]
  );
  const hiddenSoldCount = matched.length - filtered.length;

  return (
    <div>
      {/* Filter + sort toolbar */}
      <div className="flex items-center justify-between gap-2 px-4 pb-4">
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={cn(
              "inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition",
              open || activeCount > 0
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900"
            )}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="11" y1="18" x2="13" y2="18" />
            </svg>
            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
          {/* Availability. A toggle rather than a facet inside the Filters
              panel: it changes what the page is showing at a glance, and a
              shopper looking for a piece that has sold should not have to go
              hunting in a panel for it. */}
          <button
            type="button"
            onClick={() => setHideSoldOut((v) => !v)}
            aria-pressed={hideSoldOut}
            title={
              hideSoldOut
                ? "Sold pieces are hidden — show them again"
                : "Hide pieces that have already sold"
            }
            className={cn(
              "inline-flex shrink-0 items-center gap-2 rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition",
              hideSoldOut
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900"
            )}
          >
            {hideSoldOut ? "Sold Out Hidden" : "Hide Sold Out"}
          </button>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:text-slate-900"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-3">
          <label className="hidden sm:block shrink-0 text-xs uppercase tracking-[0.3em] text-slate-500" htmlFor="filtered-catalog-sort-desktop">
            Sort by
          </label>
          {/* Desktop sort */}
          <select
            id="filtered-catalog-sort-desktop"
            value={sortOption}
            onChange={(event) => setSortOption(event.target.value as SortOption)}
            className="hidden sm:block rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs uppercase tracking-[0.3em] text-slate-700"
          >
            <option value="popular">Most Popular</option>
            <option value="new-arrivals">New Arrivals</option>
            <option value="price-desc">Price - Highest to Lowest</option>
            <option value="price-asc">Price - Lowest to Highest</option>
          </select>
          {/* Mobile sort — "Sort by" as default option text replaces external label */}
          <select
            id="filtered-catalog-sort-mobile"
            value={sortOption}
            onChange={(event) => setSortOption(event.target.value as SortOption)}
            className="sm:hidden min-w-0 max-w-full truncate rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs uppercase tracking-[0.3em] text-slate-700"
          >
            <option value="popular">Sort by</option>
            <option value="new-arrivals">New Arrivals</option>
            <option value="price-desc">Price - Highest to Lowest</option>
            <option value="price-asc">Price - Lowest to Highest</option>
          </select>
        </div>
      </div>

      {/* Filter panel */}
      {open && (
        <div className="mx-4 mb-6 space-y-6 border border-slate-200 bg-white p-5">
          {showPieceFilter && (
            <FilterSection title="Piece">
              <div className="flex flex-wrap gap-2">
                {CATEGORY_CONFIG.map((cat) => (
                  <Pill
                    key={cat.slug}
                    active={pieceTypes.includes(cat.match)}
                    onClick={() => setPieceTypes(toggle(pieceTypes, cat.match))}
                  >
                    {cat.label}
                  </Pill>
                ))}
              </div>
            </FilterSection>
          )}

          <FilterSection title="Stone">
            <div className="flex flex-wrap gap-2">
              {STONE_OPTIONS.map((stone) => (
                <Pill
                  key={stone}
                  active={stones.includes(stone)}
                  onClick={() => setStones(toggle(stones, stone))}
                >
                  {stone}
                </Pill>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Stone Color">
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((c) => (
                <Pill
                  key={c.label}
                  active={colors.includes(c.label)}
                  onClick={() => setColors(toggle(colors, c.label))}
                >
                  <span className={cn("h-3 w-3 rounded-full flex-shrink-0", c.dot)} aria-hidden="true" />
                  {c.label}
                </Pill>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Metal">
            <div className="flex flex-wrap gap-2">
              {METAL_OPTIONS.map((m) => (
                <Pill
                  key={m}
                  active={metals.includes(m)}
                  onClick={() => setMetals(toggle(metals, m))}
                >
                  {m}
                </Pill>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Price">
            <PriceRangeSlider
              globalMin={globalMin}
              globalMax={globalMax}
              priceMin={priceMin}
              priceMax={priceMax}
              onMinChange={setPriceMin}
              onMaxChange={setPriceMax}
            />
          </FilterSection>
        </div>
      )}

      {/* Results */}
      {filtered.length > 0 ? (
        <CatalogClient items={filtered} backHref={backHref} backLabel={backLabel} externalSort={sortOption} />
      ) : (
        <div className="my-16 px-4">
          <div className="mx-auto max-w-md text-center space-y-5">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blush">
              <svg viewBox="0 0 24 24" className="h-6 w-6 text-royal" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-slate-900 font-[var(--font-display)]">
                {hiddenSoldCount > 0
                  ? hiddenSoldCount === 1
                    ? "The one piece here has sold"
                    : "Every piece here has sold"
                  : "Can't find what you're looking for?"}
              </h3>
              <p className="text-sm text-slate-500">
                {hiddenSoldCount > 0
                  ? "Show them anyway — we can make something similar."
                  : "If you can dream it, we can make it."}
              </p>
            </div>
            <div className="flex flex-col items-center gap-3">
              {/* Offered first when hiding sold pieces is what emptied the
                  grid — otherwise the page looks like it has nothing at all. */}
              {hiddenSoldCount > 0 && (
                <button
                  type="button"
                  onClick={() => setHideSoldOut(false)}
                  className="inline-flex items-center gap-2 rounded-sm bg-royal px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
                >
                  Show {hiddenSoldCount === 1 ? "it" : "them"}
                </button>
              )}
              <Link
                href="/custom-request"
                className={cn(
                  "inline-flex items-center gap-2 rounded-sm px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] shadow-soft transition hover:-translate-y-0.5",
                  hiddenSoldCount > 0
                    ? "border border-slate-200 bg-white text-slate-700"
                    : "bg-royal text-white"
                )}
              >
                Create a Custom Request
              </Link>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 underline underline-offset-4 transition hover:text-slate-900"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
